import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Image,
  Link,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Text as ChakraText,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import type {
  CollectionMutationDetailResponse,
  CollectionPaymentProofDetailResponse,
} from '@diamond/shared/types';
import { ProofPreviewModal } from '@diamond/shared/ui/proof-preview-modal';
import { formatCurrencyIdr } from '@diamond/shared/utils';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { type ReactNode, useEffect, useState } from 'react';

import {
  BodyCell,
  HeaderCell,
  LabelText,
  SectionTitle,
  ValueText,
} from '../../../components/mutation-drawer-typography';
import type {
  LHCDetailInvoiceRowData,
  SearchNomorMutasiRowData,
} from '../../../types';
import { getMutationDetailDrawerViewModel } from './mutation-detail-drawer.helpers';

interface MutationDetailDrawerProps {
  isOpen: boolean;
  selectedMutation: SearchNomorMutasiRowData | null;
  rowData: LHCDetailInvoiceRowData | null;
  mutationDetailData?: CollectionMutationDetailResponse | null;
  paymentProofDetailData?: CollectionPaymentProofDetailResponse | null;
  isLoading?: boolean;
  onClose: () => void;
}

interface ImagePlaceholderProps {
  message: ReactNode;
}

function ImagePlaceholder({ message }: ImagePlaceholderProps) {
  return (
    <Flex
      w="100%"
      minH="120px"
      align="center"
      justify="center"
      bg="#F2F2F7"
      borderRadius="md"
      color="#8E8E93"
      fontSize="14px"
      textAlign="center"
      px={4}
      py={6}
    >
      {message}
    </Flex>
  );
}

interface PaymentProofPreviewProps {
  proofUrl: string;
  previewKind: NonNullable<
    ReturnType<typeof getMutationDetailDrawerViewModel>['proofPreviewKind']
  >;
  imageError: boolean;
  onImageError: () => void;
  onPreviewClick?: () => void;
}

function PaymentProofPreview({
  proofUrl,
  previewKind,
  imageError,
  onImageError,
  onPreviewClick,
}: PaymentProofPreviewProps) {
  if (previewKind === 'pdf') {
    return (
      <Box
        w="270px"
        flexShrink={0}
        border="1px solid"
        borderColor="#E2E8F0"
        borderRadius="md"
        overflow="hidden"
        bg="white"
        display="flex"
        flexDirection="column"
      >
        <Box
          as="iframe"
          src={proofUrl}
          title="Bukti Transfer PDF"
          width="100%"
          height="360px"
          border="0"
        />
        <ChakraText fontSize="12px" color="#8E8E93" px={3} py={2}>
          <Link href={proofUrl} isExternal color="blue.600">
            Buka PDF di tab baru
          </Link>
        </ChakraText>
      </Box>
    );
  }

  if (previewKind === 'unsupported') {
    return (
      <Box w="270px" flexShrink={0}>
        <ImagePlaceholder
          message={
            <>
              Format file tidak didukung untuk preview.{' '}
              <Link href={proofUrl} isExternal color="blue.600">
                Buka bukti transfer
              </Link>
            </>
          }
        />
      </Box>
    );
  }

  if (imageError) {
    return (
      <Box w="270px" flexShrink={0}>
        <ImagePlaceholder
          message={
            <>
              Gagal memuat gambar.{' '}
              <Link href={proofUrl} isExternal color="blue.600">
                Buka bukti transfer
              </Link>
            </>
          }
        />
      </Box>
    );
  }

  return (
    <Button
      type="button"
      variant="unstyled"
      w="270px"
      flexShrink={0}
      h="auto"
      minH="unset"
      p={0}
      cursor="pointer"
      aria-label="Lihat bukti transfer"
      onClick={onPreviewClick}
    >
      <Image
        key={proofUrl}
        src={proofUrl}
        alt="Bukti Transfer"
        w="100%"
        h="auto"
        border="1px solid"
        borderColor="#E2E8F0"
        borderRadius="md"
        objectFit="contain"
        bg="white"
        onError={onImageError}
      />
    </Button>
  );
}

const transactionHeaders = [
  'No LHC',
  'No Surat Jalan',
  'Faktur',
  'Plant',
  'Nilai SO',
] as const;

export function MutationDetailDrawer({
  isOpen,
  selectedMutation,
  rowData,
  mutationDetailData,
  paymentProofDetailData,
  isLoading = false,
  onClose,
}: MutationDetailDrawerProps) {
  const viewModel = getMutationDetailDrawerViewModel({
    mutationDetailData,
    paymentProofDetailData,
    rowData,
    selectedMutation,
  });
  const [imageError, setImageError] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setImageError(false);
      setIsPreviewOpen(false);
    }
  }, [isOpen, viewModel.proofImageUrl]);

  const handleOpenPreview = () => {
    if (viewModel.proofImageUrl && viewModel.proofPreviewKind === 'image') {
      setIsPreviewOpen(true);
    }
  };

  return (
    <>
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay zIndex={isPreviewOpen ? 1300 : 1400} />
        <DrawerContent
          zIndex={isPreviewOpen ? 1301 : 1401}
          w="768px"
          maxW="768px"
        >
          <DrawerHeader p={4}>
            <Flex align="center" gap={2}>
              <IconButton
                aria-label="Kembali"
                icon={<ArrowBackIosNewIcon fontSize="small" />}
                variant="ghost"
                size="sm"
                onClick={onClose}
              />
              <ChakraText fontWeight={500} fontSize="20px" color="#373F41">
                Mutasi & Bukti Transfer
              </ChakraText>
            </Flex>
          </DrawerHeader>
          <DrawerCloseButton zIndex={isPreviewOpen ? 1302 : 1402} />
          <DrawerBody p={6}>
            {isLoading ? (
              <Flex minH="320px" align="center" justify="center">
                <Spinner size="lg" />
              </Flex>
            ) : viewModel.hasContent ? (
              <VStack spacing={6} align="stretch">
                <Flex justify="space-between" gap={4}>
                  <Box flex={1}>
                    <LabelText mb={1}>Nomor Mutasi</LabelText>
                    <ValueText color="blue.600">
                      {viewModel.mutationNumber}
                    </ValueText>
                  </Box>
                  <Box flex={1}>
                    <LabelText mb={1}>Total PO</LabelText>
                    <ValueText>{viewModel.poAmount}</ValueText>
                  </Box>
                  <Box flex={1}>
                    <LabelText mb={1}>Over Payment / Deposit</LabelText>
                    <ValueText>{viewModel.overPayment}</ValueText>
                  </Box>
                </Flex>

                <Flex justify="space-between" gap={4}>
                  <Box flex={1}>
                    <LabelText mb={1}>Purchase Order</LabelText>
                    <ValueText mb={2}>{viewModel.poNumber}</ValueText>
                  </Box>
                  <Box flex={1}>
                    <LabelText mb={1}>Rekap PO</LabelText>
                    <VStack align="start" spacing={1}>
                      <ChakraText fontSize="14px" color="#373F41">
                        {viewModel.relatedTransactions.length} Transaksi terkait
                      </ChakraText>
                      <ChakraText fontSize="14px" color="#373F41">
                        {viewModel.orderPaymentCount} Pembayaran
                      </ChakraText>
                      <ChakraText fontSize="14px" color="#373F41">
                        {viewModel.paymentProofCount} Bukti transfer
                      </ChakraText>
                    </VStack>
                  </Box>
                  <Box flex={1} />
                </Flex>

                <Box>
                  <SectionTitle mb={3}>Transaksi Terkait</SectionTitle>
                  <TableContainer
                    border="1px solid"
                    borderColor="#E5E5EA"
                    borderRadius="8px"
                    overflow="hidden"
                  >
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr bg="#E5E5EA !important">
                          {transactionHeaders.map((header) => (
                            <HeaderCell key={header}>{header}</HeaderCell>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {viewModel.relatedTransactions.length ? (
                          viewModel.relatedTransactions.map((transaction) => (
                            <Tr key={transaction.transaction_id}>
                              <BodyCell>{transaction.lhc_no || '-'}</BodyCell>
                              <BodyCell>
                                {transaction.delivery_order_number || '-'}
                              </BodyCell>
                              <BodyCell>
                                {transaction.billing_number || '-'}
                              </BodyCell>
                              <BodyCell>
                                {transaction.delivery_order_plant_name || '-'}
                              </BodyCell>
                              <BodyCell>
                                {formatCurrencyIdr(transaction.so_amount)}
                              </BodyCell>
                            </Tr>
                          ))
                        ) : (
                          <Tr>
                            <BodyCell colSpan={5}>
                              Belum ada transaksi terkait
                            </BodyCell>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>

                <Box>
                  <SectionTitle mb={3}>Bukti Transfer</SectionTitle>
                  <Flex gap={6} align="flex-start">
                    {viewModel.proofImageUrl && viewModel.proofPreviewKind ? (
                      <PaymentProofPreview
                        proofUrl={viewModel.proofImageUrl}
                        previewKind={viewModel.proofPreviewKind}
                        imageError={imageError}
                        onImageError={() => setImageError(true)}
                        onPreviewClick={handleOpenPreview}
                      />
                    ) : (
                      <ImagePlaceholder message="Tidak ada bukti transfer" />
                    )}

                    <VStack align="stretch" spacing={6} flex={1}>
                      <Flex gap={8}>
                        <Box flex={1}>
                          <LabelText mb={1}>Purchase Order</LabelText>
                          <ValueText fontSize="16px">
                            {viewModel.poNumber}
                          </ValueText>
                        </Box>
                        <Box flex={1}>
                          <LabelText mb={1}>Nomor Rekening</LabelText>
                          <ValueText fontSize="16px">
                            {viewModel.bankAccount}
                          </ValueText>
                        </Box>
                      </Flex>

                      <Flex gap={8}>
                        <Box flex={1}>
                          <LabelText mb={1}>Jumlah Bukti</LabelText>
                          <ValueText fontSize="16px" wordBreak="break-word">
                            {String(viewModel.paymentProofCount)}
                          </ValueText>
                        </Box>
                        <Box flex={1}>
                          <LabelText mb={1}>Bank</LabelText>
                          <ValueText fontSize="16px">
                            {viewModel.bankName}
                          </ValueText>
                        </Box>
                      </Flex>
                    </VStack>
                  </Flex>
                </Box>
              </VStack>
            ) : null}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {viewModel.proofImageUrl ? (
        <ProofPreviewModal
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          fileUrl={viewModel.proofImageUrl}
          zIndex={1600}
        />
      ) : null}
    </>
  );
}
